<template>
  <v-app>
    <!-- Loading Overlay Global -->
    <LoadingOverlay
        :value="(isLoading && logado) || isLogoutLoading"
        :message="loadingMessage"
        :subtitle="loadingSubtitle"
    />

    <!-- Tela de Login -->
    <transition name="fade" mode="out-in">
      <template v-if="!logado">
        <v-main style="background-color: #023449;" key="login">
          <Login />
        </v-main>
      </template>

      <!-- Aplicação Logada -->
      <template v-else>
        <div key="app" class="app-wrapper">
          <!-- Dialog de Primeiro Acesso -->
          <LoginDialog v-if="usuario.flagprimeiroacesso" />

          <!-- Menu Principal -->
          <MenuPrincipal
              :value="showMenu"
              :usuario="usuario"
              @logout="realizarLogout"
          />

          <!-- Conteúdo Principal -->
          <transition name="fade-content" mode="out-in">
            <v-main v-show="showContent" class="main-content">
              <v-container fluid class="pa-2 pa-sm-4 app-container">
                <keep-alive>
                  <router-view />
                </keep-alive>
              </v-container>

              <!-- Footer -->
              <v-footer color="transparent" elevation="0" class="py-6">
                <v-container>
                  <v-row align="center" justify="center">
                    <v-col cols="12" class="text-center">
                      <img height="55" src="./assets/icons/logo_footer.svg" alt="Logo" />
                      <p class="text-caption grey--text mt-3 mb-0">
                        © {{ new Date().getFullYear() }} - Sistema Gestor
                      </p>
                    </v-col>
                  </v-row>
                </v-container>
              </v-footer>
            </v-main>
          </transition>
        </div>
      </template>
    </transition>
  </v-app>
</template>

<script>
import Login from "./Components/Login/Login.vue";
import LoginDialog from "./Components/Login/LoginDialog.vue";
import MenuPrincipal from "./Components/Widgets/MenuPrincipal.vue";
import LoadingOverlay from "./Components/Widgets/CarregamentoGestor.vue";
import axios from "axios";
import { mapMutations, mapState } from "vuex";
import { globalMixin } from "./globalMixin";

export default {
  components: {
    Login,
    LoginDialog,
    MenuPrincipal,
    LoadingOverlay
  },

  mixins: [globalMixin],

  data: () => ({
    showMenu: false,
    showContent: false,
    // Estados de loading
    isLoading: false,
    loadingMessage: 'Carregando',
    loadingSubtitle: 'Por favor, aguarde...',
    loadingStartTime: null,
    minLoadingTime: 1500,
    // Controle de sessão expirada
    sessionExpiredTimeout: null,
    // Loading especial para logout (sempre visível)
    isLogoutLoading: false,
  }),

  computed: {
    ...mapState([
      "usuario",
      "sessaoChave",
      "backendUrl",
      "logado",
      "primeiroAcesso",
    ]),
  },

  methods: {
    ...mapMutations(["uLogar"]),

    /**
     * Mostra o loading global
     */
    showLoading(message = 'Carregando', subtitle = 'Por favor, aguarde...') {
      this.loadingMessage = message;
      this.loadingSubtitle = subtitle;
      this.loadingStartTime = Date.now();
      this.isLoading = true;
    },

    /**
     * Esconde o loading global
     */
    async hideLoading() {
      if (this.loadingStartTime) {
        const elapsedTime = Date.now() - this.loadingStartTime;
        const remainingTime = this.minLoadingTime - elapsedTime;

        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
      }

      this.isLoading = false;
      this.loadingStartTime = null;
    },

    /**
     * Valida o token do usuário no backend
     */
    async validarToken() {
      const json = localStorage.getItem(this.sessaoChave);
      const sessao = JSON.parse(json);

      if (!sessao) {
        this.$store.commit("uLogar", null);
        throw new Error('Sessão não encontrada');
      }

      axios.defaults.headers.common["token"] = sessao.token;

      try {
        const res = await axios.post(
            `${this.backendUrl}login/sessao_token_validar`,
            { token: sessao.token }
        );

        // Verificar se há erro de token inválido na resposta
        if (res.data && res.data.erro) {
          const erroMsg = res.data.erro.trim().toLowerCase();
          if (erroMsg.includes('token') && erroMsg.includes('inválido')) {
            this.handleSessionExpired();
            throw new Error('Token inválido');
          }
          throw new Error(res.data.erro);
        }

        if (res.data) {
          this.$store.commit("uLogar", {
            ...sessao,
            logado: true,
          });
          return true;
        } else {
          localStorage.removeItem(this.sessaoChave);
          delete axios.defaults.headers.common['token'];
          this.$store.commit("uLogar", null);
          throw new Error('Token inválido');
        }
      } catch (error) {
        // Verificar se é erro de resposta HTTP
        if (error.response) {
          const status = error.response.status;
          const errorData = error.response.data;

          // Verificar mensagem de erro no body
          if (errorData && errorData.erro) {
            const erroMsg = errorData.erro.trim().toLowerCase();
            if (erroMsg.includes('token') && erroMsg.includes('inválido')) {
              this.handleSessionExpired();
              throw error;
            }
          }

          // Verificar status codes de autenticação
          if (status === 401 || status === 403) {
            this.handleSessionExpired();
            throw error;
          }
        }

        // Limpar sessão em qualquer erro
        localStorage.removeItem(this.sessaoChave);
        delete axios.defaults.headers.common['token'];
        this.$store.commit("uLogar", null);
        throw error;
      }
    },

    /**
     * Manipula sessão expirada com mensagem e redirecionamento
     */
    handleSessionExpired() {
      // Limpar timeout anterior se existir
      if (this.sessionExpiredTimeout) {
        clearTimeout(this.sessionExpiredTimeout);
      }

      // Limpar dados da sessão
      localStorage.removeItem(this.sessaoChave);
      delete axios.defaults.headers.common['token'];
      this.$store.commit("uLogar", null);

      // Ocultar conteúdo e menu
      this.showContent = false;
      this.showMenu = false;

      // Mostrar mensagem de sessão expirada
      this.showLoading(
          'Sessão Expirada',
          'Sua sessão expirou. Você será redirecionado para o login em 5 segundos...'
      );

      // Redirecionar após 5 segundos
      this.sessionExpiredTimeout = setTimeout(() => {
        this.hideLoading();
        window.location.reload();
      }, 5000);
    },

    /**
     * Realiza o logout do usuário
     */
    async realizarLogout() {
      try {
        // Ativa loading especial de logout (independente do estado logado)
        this.isLogoutLoading = true;
        this.loadingMessage = 'Saindo';
        this.loadingSubtitle = 'Até logo!';

        // Aguarda um pouco para o usuário ver o loading
        await new Promise(resolve => setTimeout(resolve, 500));

        this.showContent = false;
        this.showMenu = false;

        // Tenta chamar endpoint de logout (não bloqueia se falhar)
        try {
          await axios.post(`${this.backendUrl}login/sair`, this.usuario);
        } catch (error) {
          console.warn('Erro ao chamar endpoint de logout:', error);
        }

        // Limpa sessão local
        localStorage.removeItem(this.sessaoChave);
        this.$store.commit("uLogar", null);
        delete axios.defaults.headers.common['token'];

        // Remove a flag para forçar reload na próxima vez que cair no login
        sessionStorage.removeItem('login_reload_done');

        // Recarrega a página (vai para tela de login e limpa cache)
        location.reload();
      } catch (error) {
        console.error('Erro fatal ao fazer logout:', error);
        // Em caso de erro crítico, força o reload mesmo assim
        location.reload();
      }
    },

    /**
     * Inicializa a aplicação validando token
     */
    async init() {
      this.showLoading('Inicializando', 'Preparando o sistema...');

      // Tenta validar token primeiro (não redireciona se falhar)
      try {
        await this.validarToken();
      } catch (error) {
        // Se falhar, apenas loga o erro sem redirecionar ainda
        // console.log('Token não validado:', error.message);
      }

      if (this.logado) {
        // Usuário ESTÁ logado - limpa a flag de reload
        sessionStorage.removeItem('login_reload_done');

        await this.$nextTick();
        setTimeout(() => {
          // em telas pequenas o menu deve começar fechado (mesmo limiar usado no MenuPrincipal.vue,
          // checkDevice/isMobile) — sem essa checagem, este showMenu = true sobrescrevia o comportamento
          // "começa fechado" que o próprio MenuPrincipal já tenta aplicar sozinho
          this.showMenu = window.innerWidth >= 960;
        }, 100);
        setTimeout(() => {
          this.showContent = true;
          this.hideLoading();
        }, 400);
      } else {
        // Usuário NÃO está logado - verifica se precisa recarregar
        this.recarregarLoginSeNecessario();
      }
    },

    //Recarrega a página uma única vez quando cai na tela de login
    recarregarLoginSeNecessario() {
      const reloadDone = sessionStorage.getItem('login_reload_done');

      if (!reloadDone) {
        // console.log('🔄 Primeira vez na tela de login - recarregando para limpar cache...');

        // Marca que o reload já foi feito
        sessionStorage.setItem('login_reload_done', 'true');

        // Recarrega a página (limpa cache automaticamente)
        location.reload();
      } else {
        // Já recarregou - apenas mostra o login
        // console.log('✅ Cache já limpo - exibindo login');
        this.hideLoading();
      }
    },
  },

  mounted() {
    this.init();

    // Interceptor de requisições
    axios.interceptors.request.use((config) => {
      return config;
    });

    // Interceptor de respostas
    axios.interceptors.response.use(
        (response) => {
          return response;
        },
        (error) => {
          // Verificar erros de token em todas as requisições
          if (error.response) {
            const status = error.response.status;
            const errorData = error.response.data;

            if (errorData && errorData.erro) {
              const erroMsg = errorData.erro.trim().toLowerCase();
              if (erroMsg.includes('token') && erroMsg.includes('inválido')) {
                this.handleSessionExpired();
              }
            }

            if (status === 401 || status === 403) {
              this.handleSessionExpired();
            }
          }
          return Promise.reject(error);
        }
    );

    // Listener para navegação do router
    this.$router.beforeEach(async (to, from, next) => {
      // Se está indo para login, permite sem validação
      if (to.path === '/login') {
        return next();
      }

      // Verifica se tem sessão no localStorage
      const json = localStorage.getItem(this.sessaoChave);
      const sessao = JSON.parse(json);

      // Se não tem sessão e não está indo para login, redireciona
      if (!sessao) {
        return next('/login');
      }

      // Se está logado e navegando entre páginas
      if (to.path !== from.path && this.logado) {
        this.showLoading('Carregando página', 'Aguarde um momento...');

        try {
          // Valida token antes de cada navegação
          await this.validarToken();
          next();
        } catch (error) {
          // Se a validação falhar, redireciona para login
          console.error('Erro na validação do token:', error);
          next('/login');
        }
      } else {
        next();
      }
    });

    this.$router.afterEach(() => {
      setTimeout(() => {
        this.hideLoading();
      }, 500);
    });
  },

  beforeDestroy() {
    // Limpar timeout se o componente for destruído
    if (this.sessionExpiredTimeout) {
      clearTimeout(this.sessionExpiredTimeout);
    }
  },

  watch: {
    logado(novoValor) {
      if (novoValor) {
        this.init();
      } else {
        this.showMenu = false;
        this.showContent = false;
      }
    }
  },
};
</script>

<style>
/* Previne scroll horizontal sem criar BFC (html/body são elementos especiais) */
html {
  overflow-x: hidden;
}
</style>

<style scoped>
/* Transições */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.fade-enter {
  opacity: 0;
  transform: scale(0.98);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(1.02);
}

.fade-content-enter-active {
  transition: opacity 0.4s ease 0.2s;
}

.fade-content-leave-active {
  transition: opacity 0.3s ease;
}

.fade-content-enter {
  opacity: 0;
}

.fade-content-leave-to {
  opacity: 0;
}

.app-wrapper {
  width: 100%;
  height: 100%;
}

.main-content {
  background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
  min-height: 100vh;
}

.v-footer {
  background: transparent !important;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.app-container {
  max-width: 100%;
}

</style>